import { Sha256 } from '@aws-crypto/sha256-js'
import async from 'async'
import axios, { CanceledError } from 'axios'
import { ensureWebStreamsPolyfill } from 'src/utils/polyfills/web-streams'
import { executeUploadFileMultiPart } from '../apollo/file'
import type {
  UploadFileMultiPartMutation,
  UploadPartType,
} from '../apollo/generated/graphql'
import { UploadMultiPartStep } from '../apollo/generated/graphql'

const CHUNK_SIZE = 5 * 1024 * 1024 // 5MiB

const base64encode = (buffer: Uint8Array) =>
  btoa(String.fromCharCode(...buffer))

function blobToReadableStream(blob: Blob) {
  const reader = blob.stream().getReader()
  return new ReadableStream({
    start(controller) {
      function push() {
        reader.read().then(({ done, value }) => {
          if (done) {
            controller.close()
            return
          }
          controller.enqueue(value)
          push()
        })
      }
      push()
    },
  })
}

export const blobSha256Base64 = async (
  file: Blob,
  opt?: {
    abortSignal?: AbortSignal
  },
) => {
  await ensureWebStreamsPolyfill()
  const stream = blobToReadableStream(file)

  const hash = new Sha256()
  for await (const chunk of stream) {
    if (opt?.abortSignal?.aborted) throw new Error('Hash aborted')
    hash.update(chunk)
  }
  const buffer = await hash.digest()
  return base64encode(buffer)
}

export async function uploadFileMultiPart(
  file: File,
  hash: string,
  opts?: {
    scope?: 'custom'
    onProgress?: (percentage: number) => void
    abortSignal?: AbortSignal
  },
) {
  if (opts?.abortSignal?.aborted) throw new Error('Aborted')

  const { name: filename, type: fileType, size: fileSize } = file
  const fileMeta = {
    fileSize: 0, // FIXME: non-safe integer causes error in backend
    filename,
    fileType,
  }

  const step1Res = await executeUploadFileMultiPart(
    {
      step: UploadMultiPartStep.Init,
      input: {
        fileMeta,
        hash,
        scope: opts?.scope,
      },
    },
    {
      context: {
        headers: {
          checksum: 'v2',
        },
      },
    },
  )

  const step1Data = step1Res.data?.uploadFileMultiPart
  const { uploadId, exists } = step1Data ?? {}

  if (!uploadId) throw new Error('uploadId invalid')

  if (exists) return step1Data

  let uploadedBytes = 0
  const splitAmount = Math.ceil(fileSize / CHUNK_SIZE)
  const partNums = Array.from(Array(splitAmount).fill(''), (_, idx) => idx + 1)

  const uploadPart = async (partNum: number): Promise<UploadPartType> => {
    const chunk = file.slice(
      (partNum - 1) * CHUNK_SIZE,
      partNum === partNums.length ? fileSize : partNum * CHUNK_SIZE,
    )
    const hash = await blobSha256Base64(chunk)

    const partRes = await executeUploadFileMultiPart({
      step: UploadMultiPartStep.UploadPart,
      input: {
        fileMeta,
        uploadId,
        partNumber: partNum,
        hash,
      },
    })
    const { uploadUrl } = partRes.data?.uploadFileMultiPart ?? {}
    if (!uploadUrl) throw new Error('uploadUrl invalid')

    const s3Res = await axios.put(uploadUrl, chunk, {
      headers: {
        'x-amz-checksum-sha256': hash,
      },
      signal: opts?.abortSignal,
      responseType: 'text',
      onUploadProgress(progressEvent) {
        if (opts?.abortSignal?.aborted) return
        uploadedBytes += progressEvent.bytes
        opts?.onProgress?.((uploadedBytes - 1) / fileSize) // prevent reaching 100%
      },
    })

    if (s3Res.status > 299) {
      console.error('upload failed', s3Res.data)
      throw new Error('Upload failed: ' + s3Res.status)
    }

    const etag = s3Res.headers['etag']
    if (!etag) throw new Error('ETag invalid')
    return { eTag: etag.replaceAll('"', ''), partNumber: partNum, hash }
  }

  const parts: UploadPartType[] = await async.mapLimit(
    partNums,
    5,
    async (partNum: number) => {
      const res = await async.retry(
        {
          times: 5,
          interval(retryCount) {
            return 50 * Math.pow(2, retryCount)
          },
          errorFilter(error) {
            // If user cancels the upload, don't retry uploading
            if (axios.isAxiosError(error) && error instanceof CanceledError)
              return false
            return axios.isAxiosError(error) && error.response?.status !== 400
          },
        },
        async () => await uploadPart(partNum),
      )
      return res
    },
  )

  await executeUploadFileMultiPart({
    step: UploadMultiPartStep.Complete,
    input: {
      fileMeta,
      parts,
      uploadId,
      hash,
    },
  })
  return {
    uploadId,
  } satisfies UploadFileMultiPartMutation['uploadFileMultiPart']
}
